import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TableContainer, Typography } from '@mui/material';
import { Header, LwButton, Page, Toolbox, useFormButtons } from 'redesign';
import { routes } from 'ogp/Routes';
import { useCompanyRelativePath } from 'ogp/hooks';
import { RenderUnsettledUI } from 'shared/components';
import { useAlert } from 'shared/components/alerts';
import {
  ContractorCompanyRate,
  UpdateContractorCompanyRate,
} from 'api/services/company/company-service.schema';
import { createRateDrafts, parseRateCents, rateDraftKey } from './contractor-rate-values';
import { ContractorRatesEditTable } from './contractor-rates-edit-table';
import { useGetContractorCompanyRates } from './hooks/use-get-contractor-company-rates';
import { useUpdateContractorCompanyRates } from './hooks/use-update-contractor-company-rates';
import { useContractorRateColumns } from './use-contractor-rate-columns';

const CompanyContractorRatesEdit = () => {
  const query = useGetContractorCompanyRates();

  if (query.status !== 'success') {
    return <RenderUnsettledUI data={query} />;
  }

  return <CompanyContractorRatesEditForm jobTypes={query.data.jobTypes} />;
};

const CompanyContractorRatesEditForm = ({ jobTypes }: { jobTypes: ContractorCompanyRate[] }) => {
  const { t } = useTranslation(['ogp-screen-specific', 'web-common']);
  const { alertSuccess, alertError } = useAlert();
  const navigate = useNavigate();
  const generatePath = useCompanyRelativePath();
  const mutation = useUpdateContractorCompanyRates();
  const { submitButtonProps, cancelButtonProps, returnTo } = useFormButtons(mutation.status);
  const { columns } = useContractorRateColumns();
  const [drafts, setDrafts] = useState(() => createRateDrafts(jobTypes));

  const returnToCompany = () => {
    navigate(generatePath(routes.Company));
  };

  const saveRates = () => {
    if (mutation.isLoading) {
      return;
    }

    const changes: UpdateContractorCompanyRate[] = [];
    for (const jobType of jobTypes) {
      for (const column of columns) {
        const draft = drafts[rateDraftKey(jobType.jobTypeId, column.employmentType)] ?? '';
        const parsedRateCents = parseRateCents(draft);
        if (parsedRateCents === undefined) {
          alertError(
            new Error(
              t('company.contractor-rates.rate.error', {
                defaultValue: 'Vul een geldig uurtarief in.',
              })
            )
          );
          return;
        }

        if (parsedRateCents !== column.rateCents(jobType)) {
          changes.push({
            jobTypeId: jobType.jobTypeId,
            employmentType: column.employmentType,
            rateCents: parsedRateCents,
          });
        }
      }
    }

    if (changes.length === 0) {
      returnToCompany();
      return;
    }

    mutation.mutate(changes, {
      onSuccess: () => {
        alertSuccess(
          t('company.contractor-rates.save-success.description', {
            defaultValue: 'De uurtarieven zijn opgeslagen.',
          })
        );
        returnToCompany();
      },
      onError: (error) => alertError(error),
    });
  };

  return (
    <Page
      header={
        <Header
          titleText={t('company.contractor-rates.edit-page.label', {
            defaultValue: 'Uurtarieven aanpassen',
          })}
        />
      }
      toolbox={
        <Toolbox underline>
          <LwButton onClick={saveRates} iconColorMode="stroke" {...submitButtonProps}>
            {t('buttons.confirm', { ns: 'web-common' })}
          </LwButton>
          <LwButton onClick={returnTo(routes.Company)} iconColorMode="fill" {...cancelButtonProps}>
            {t('buttons.cancel', { ns: 'web-common' })}
          </LwButton>
        </Toolbox>
      }
    >
      <TableContainer>
        <Typography variant="body2" color="text.secondary">
          {t('company.contractor-rates.edit-intro.description', {
            defaultValue:
              'Vul per functie het uurtarief in. Laat een veld leeg om het tarief te verwijderen.',
          })}
        </Typography>
        <ContractorRatesEditTable
          jobTypes={jobTypes}
          drafts={drafts}
          disabled={mutation.isLoading}
          onRateChange={(draftKey, value) =>
            setDrafts((current) => ({ ...current, [draftKey]: value }))
          }
        />
      </TableContainer>
    </Page>
  );
};

export { CompanyContractorRatesEdit };
